<script setup lang="ts">
import { MtdButton, MtdModalMobile, MtdTypography } from '@paygine/mtd-uikit'
import { ref, useTemplateRef } from 'vue'

import { userDataApi } from '@/entities/user-data'
import { carNumberModel } from '@/entities/v-car-number'
import { useTransport } from '@/features/transport'
import { AttentionIcon } from '@/shared/icons'
import { formatCarNumber, transliterateCarNumber } from '@/shared/lib/formatting'
import AddTransport from './AddTransport.vue'
import TransportItem from './TransportItem.vue'

const props = defineProps<{
    transportList?: userDataApi.UserData['grn_list']
}>()

const { addTransport, deleteTransport, isLoading } = useTransport()

const externalError = ref<carNumberModel.ExternalError>({})
const currentCar = ref<null | Partial<userDataApi.Grn>>(null)
const addTransportRef = useTemplateRef<InstanceType<typeof AddTransport>>('add-transport')

async function addCarHandler() {
    const grn = currentCar.value?.grn
    const grnCountry = currentCar.value?.grn_country

    if (!grn || !grnCountry) return
    if (!(await addTransportRef.value?.validate())) return

    const result = await addTransport({ grn, grn_country: grnCountry })

    if (result?.success && result?.id) {
        isAddWidgetOpen.value = false
        return
    } else if (result.mask_phone) {
        externalError.value.alreadyLinkedPhone = result.mask_phone
        return
    }

    externalError.value.unknownError = true
}

async function deleteCarHandler() {
    if (!currentCar.value?.id || !props.transportList?.length) return

    await deleteTransport(currentCar.value.id, currentCar.value.grn || '')

    isDeleteModalOpen.value = false
}

function onDelete(car: userDataApi.Grn) {
    isDeleteModalOpen.value = true
    currentCar.value = car
}

function updateCarNumber(payload: { country: string; grn: string }) {
    currentCar.value = {
        grn: payload.grn,
        grn_country: payload.country,
    }
    externalError.value = {}
}

const isAddWidgetOpen = ref(false)
const isDeleteModalOpen = ref(false)
const isAddModalReady = ref(false)

function openAddWidget() {
    externalError.value = {}
    isAddWidgetOpen.value = true
    isAddModalReady.value = false

    //костыль из-за неправильного рассчета позиции Popup в модалке
    setTimeout(() => {
        isAddModalReady.value = true
    }, 250) // если анимация модалки 200ms, добавляем запас
}
</script>

<template>
    <div class="transport-management">
        <div class="transport-management__header">
            <MtdTypography
                variant="textbody-1-semi"
                class="transport-management__title"
                tag="h2"
                >Мой транспорт
            </MtdTypography>

            <MtdButton
                v-if="props.transportList?.length"
                variant="main-flat"
                type="button"
                class="transport-management__header-button"
                size="xs"
                @click="openAddWidget"
                >Добавить</MtdButton
            >
        </div>

        <div
            v-if="props.transportList?.length"
            class="transport-list"
        >
            <TransportItem
                v-for="transport in props.transportList"
                :key="transport.id"
                :transport
                @transport:delete="onDelete"
            >
            </TransportItem>
        </div>

        <MtdButton
            v-if="!props.transportList?.length"
            class="transport-management__add-button"
            variant="main-secondary"
            type="button"
            @click="openAddWidget"
            >Добавить</MtdButton
        >

        <AddTransport
            :external-error
            @car-number:add="addCarHandler"
            @car-number:update="updateCarNumber"
            :is-loading
            class="mt-16"
            v-model="isAddWidgetOpen"
            ref="add-transport"
        />

        <MtdModalMobile
            v-model="isDeleteModalOpen"
            class="transport-modal"
            container-class="transport-modal__container transport-modal__container--delete"
        >
            <template #content>
                <AttentionIcon />

                <MtdTypography
                    variant="header-6"
                    class="default-layout__title mt-16"
                    tag="h1"
                    >Удалить
                    {{
                        currentCar?.grn_country === 'RUS'
                            ? formatCarNumber(
                                  transliterateCarNumber(currentCar?.grn || '', 'latin-to-cyrillic')
                              )
                            : currentCar?.grn
                    }}
                    ?
                </MtdTypography>

                <div class="transport-modal__button-container">
                    <MtdButton
                        class="mt-24"
                        variant="main-secondary"
                        @click="isDeleteModalOpen = false"
                        >Назад</MtdButton
                    >

                    <MtdButton
                        class="mt-24"
                        variant="danger-secondary"
                        :is-loading="isLoading"
                        @click="deleteCarHandler"
                        >Удалить</MtdButton
                    >
                </div>
            </template>
        </MtdModalMobile>
    </div>
</template>

<style src="./TransportManagement.scss" lang="scss"></style>
